import UserContext from '@/context/UserContext';
import {
  NAME_ORAI_EVM,
  ORAI_EVM_CHAIN_ID,
  ORAI_EVM_CHAIN_ID_NEW_FORMAT,
  RPC_ORAI_EVM,
} from '@/rpcConfig';
import { UserInfo } from '@/utils/types';
import { confirmWallet, handleLogin } from '@/utils/util';
import { usePersistActions } from '@/zustand-store/persist-config/selector';
import usePersistStore from '@/zustand-store/persist-config/usePersistStore';
import { isMobile } from '@walletconnect/browser-utils';
import { ethers } from 'ethers';
import { useContext, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useDisconnect } from 'wagmi';
import useWindowSize from './useWindowSize';
import { Web3MappingEvmInteraction } from '@/utils/mappingEvmAddr';
import { successAlert } from '@/components/others/ToastGroup';

export const WALLET_TYPE = {
  META_MASK: 'META_MASK',
  OWALLET: 'OWALLET',
};

const web3MappingEvm = new Web3MappingEvmInteraction();
export const triggerUnlockOwalletInEvmNetwork = async () => {
  try {
    return await window.owallet.enable(ORAI_EVM_CHAIN_ID_NEW_FORMAT);
  } catch (error) {
    if (error.message === 'There is no chain info for eip155:4143398064') {
      await window.eth_owallet.request({
        method: 'wallet_addEthereumChain',
        params: [
          {
            chainName: NAME_ORAI_EVM,
            chainId: ORAI_EVM_CHAIN_ID,
            nativeCurrency: { name: 'ORAI', decimals: 18, symbol: 'ORAI' },
            rpcUrls: [RPC_ORAI_EVM],
            iconUrls:
              ["https://raw.githubusercontent.com/chainapsis/keplr-chain-registry/main/images/Oraichain/chain.png"]
          },
        ],
      });
      await window.owallet.enable(ORAI_EVM_CHAIN_ID_NEW_FORMAT);
    } else throw new Error(error);
  }
};

// EIP-6963 Interface definitions
interface EIP6963ProviderInfo {
  uuid: string;
  name: string;
  icon: string;
  rdns: string;
}

interface EIP6963ProviderDetail {
  info: EIP6963ProviderInfo;
  provider: any;
}

interface EIP6963AnnounceProviderEvent extends CustomEvent {
  type: 'eip6963:announceProvider';
  detail: EIP6963ProviderDetail;
}

/**
 * Hook to handle EIP-6963 providers
 */
export function useEIP6963Providers() {
  const [providers, setProviders] = useState<
    Map<string, EIP6963ProviderDetail>
  >(new Map());

  useEffect(() => {
    const handlers = new Map<string, EIP6963ProviderDetail>();

    // Handler for when providers announce themselves
    function handleAnnouncement(event: EIP6963AnnounceProviderEvent) {
      handlers.set(event.detail.info.uuid, event.detail);
      setProviders(new Map(handlers));
    }

    // Request providers to announce themselves
    function requestProviders() {
      window.dispatchEvent(new Event('eip6963:requestProvider'));
    }

    // Add event listener
    window.addEventListener(
      'eip6963:announceProvider',
      handleAnnouncement as EventListener,
    );

    // Initial request
    requestProviders();

    return () => {
      window.removeEventListener(
        'eip6963:announceProvider',
        handleAnnouncement as EventListener,
      );
    };
  }, []);

  return providers;
}

/**
 * Get specific provider using EIP-6963
 */
export function getSpecificEIP6963Provider(
  providers: Map<string, EIP6963ProviderDetail>,
  walletName: string,
): any {
  if (!providers) return null;
  for (const [_, detail] of providers) {
    if (detail.info.name.toLowerCase().includes(walletName.toLowerCase())) {
      return detail.provider;
    }
  }
  return null;
}

export const switchNetwork = async (
  chainIdHex: string = ORAI_EVM_CHAIN_ID,
  options: any = {
    chainName: NAME_ORAI_EVM,
    chainId: chainIdHex,
    nativeCurrency: { name: 'ORAI', decimals: 18, symbol: 'ORAI' },
    rpcUrls: [RPC_ORAI_EVM]
  },
) => {
  const providers = usePersistStore.getState().evmProviders;
  const provider = getSpecificEIP6963Provider(providers, 'metamask');
  if (!provider) throw new Error('MetaMask not found');
  try {
    await provider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: '0x' + Number(chainIdHex).toString(16) }],
    });
  } catch (err) {
    if (err.code === 4902) {
      await provider.request({
        method: 'wallet_addEthereumChain',
        params: [options],
      });
    }
  }
};

export const getEvmProvider = () => {
  const evmTypeWallet = usePersistStore.getState().evmTypeWallet;
  const providers = usePersistStore.getState().evmProviders;
  if (evmTypeWallet === WALLET_TYPE.META_MASK) {
    return getSpecificEIP6963Provider(providers, 'metamask');
  } else if (evmTypeWallet === WALLET_TYPE.OWALLET) {
    const isMobileMode = isMobile();
    return isMobileMode ? window.ethereum : window.eth_owallet;
  }
};

export const useEvmConnect = () => {
  const [loadingConnectMetamask, setLoadingConnectMetamask] =
    useState<boolean>(false);
  const [loadingConnectOwalletEVM, setLoadingConnectOwalletEVM] =
    useState<boolean>(false);
  const { disconnectAsync } = useDisconnect();
  const { isMobileMode } = useWindowSize();
  const { setUser, setEvmTypeWallet, setEvmProviders } = usePersistActions();
  const { setLogin } = useContext(UserContext);
  const providers = useEIP6963Providers();

  useEffect(() => {
    setEvmProviders(providers);
  }, [providers]);

  const confirmWalletWithSigature = async (connection) => {
    const msg = `Sign in to Agent.land: ${connection.nonce}`;
    const msgHex = `0x${Buffer.from(msg, 'utf8').toString('hex')}`;
    let sig = '';
    try {
      const provider = getEvmProvider();
      sig = await provider.request({
        method: 'personal_sign',
        params: [msgHex, connection.wallet],
      });
    } catch (error) {
      throw new Error(error);
    }

    const signedWallet = { ...connection, signature: sig };
    return confirmWallet({ data: signedWallet });
  };

  const connectOwalletEVM = async () => {
    setLoadingConnectOwalletEVM(true);
    const isOwallet = isMobileMode
      ? window.ethereum.isOWallet
      : //@ts-ignore
      window.eth_owallet && window.owallet.isOwallet;
    if (!isOwallet) {
      if (isMobileMode) {
        const deepLinkApp = 'https://owallet.io/';
        toast.info(`Please open the application in Owallet's browser`);
        setTimeout(() => {
          window.open(deepLinkApp, '_blank');
        }, 1000);
        return;
      }

      toast.warning(`Please install Owallet to continue!`);
      return;
    }

    try {
      const ethereumProvider = isMobileMode
        ? window?.ethereum
        : window?.eth_owallet;
      if (!ethereumProvider) {
        return toast.warning(`Please install Owallet to continue!`);
      }

      if (!isMobileMode) {
        await triggerUnlockOwalletInEvmNetwork();

        // passe cointype 60 for ethereum or let it use default param
        let accounts = await window.eth_owallet.request({
          method: 'eth_accounts',
          params: [60],
        });
        if (accounts.length === 0) {
          accounts = await window.eth_owallet.request({
            method: 'eth_requestAccounts',
            params: [],
          });
        }
        const evmAddress = ethers.getAddress(accounts[0]);
        setEvmTypeWallet(WALLET_TYPE.OWALLET);

        const updatedUser: UserInfo = {
          name: evmAddress.slice(0, 6),
          wallet: evmAddress,
          isLedger: false,
        };
        await handleLogin({
          updatedUser,
          setUser,
          setLogin,
          confirmWalletWithSigature,
        });
        const data = await web3MappingEvm.mappingAddress(evmAddress);
        if (data) {
          successAlert(data.message);
        }
      }
    } catch (error: any) {
      console.error(error, 'error');
      setEvmTypeWallet('');
      toast.error(error?.message);
    } finally {
      setLoadingConnectOwalletEVM(false);
    }
  };

  const connectMetamaskWallet = async () => {
    setLoadingConnectMetamask(true);

    try {
      const provider = getSpecificEIP6963Provider(providers, 'metamask');

      if (!provider) {
        if (isMobileMode) {
          const deepLinkApp = `https://metamask.app.link/dapp/${document.location.host}`;
          toast.info(`Please open the application in MetaMask's browser`);
          setTimeout(() => {
            window.open(deepLinkApp, '_blank');
          }, 1000);
          return;
        }
        return toast.warning(`Please install MetaMask to continue!`);
      }

      // Switch network
      await switchNetwork(ORAI_EVM_CHAIN_ID);

      // Request accounts using the EIP-6963 provider
      let accounts = await provider.request({
        method: 'eth_accounts',
        params: [60],
      });

      if (accounts.length === 0) {
        accounts = await provider.request({
          method: 'eth_requestAccounts',
          params: [],
        });
      }

      const evmAddress = ethers.getAddress(accounts[0]);
      const updatedUser: UserInfo = {
        name: evmAddress.slice(0, 6),
        wallet: evmAddress,
        isLedger: false,
      };

      setEvmTypeWallet(WALLET_TYPE.META_MASK);
      const data = await web3MappingEvm.mappingAddress(evmAddress);
      if (data) {
        successAlert(data.message);
      }
      await handleLogin({
        updatedUser,
        setUser,
        setLogin,
        confirmWalletWithSigature,
      });


    } catch (error: any) {
      console.error('MetaMask connection error:', error);
      setEvmTypeWallet('');
      toast.error(error?.message || 'Failed to connect to MetaMask');
    } finally {
      setLoadingConnectMetamask(false);
    }
  };

  return {
    loading: loadingConnectMetamask || loadingConnectOwalletEVM,
    loadingConnectOwalletEVM,
    loadingConnectMetamask,
    connectMetamaskWallet,
    connectOwalletEVM,
    logout: disconnectAsync,
    confirmWalletWithSigature,
  };
};
